<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.entryRejectForm.heading",
                "Reject entry on behalf of OC"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.entryRejectForm.explanation.text",
                "You are going to reject the following entry on behalf of OC."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.entryRejectForm.competitionCode.label",
                "Entry"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t(
                "events:entryAction.entryRejectForm.athleteNames.label",
                "Athlete"
              )
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t("events:entryAction.entryRejectForm.horseNames.label", "Horse")
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <h3>
          {{
            $t(
              "events:entryAction.entryRejectForm.comments.heading",
              "Comments"
            )
          }}
        </h3>
        <p>
          {{
            $t(
              "events:entryAction.entryRejectForm.comments.text",
              "Add comments. Comment to athlete will be sent to her/his registration responsible after rejection is confirmed. Internal comment will remain visible for administrators in the event masterlist."
            )
          }}
        </p>

        <v-textarea
          v-model="noteToParticipant"
          :label="
            $t(
              'events:entryAction.entryRejectForm.comments.noteToParticipant.label',
              'Comment to athlete'
            )
          "
          :hint="
            $t(
              'events:entryAction.entryRejectForm.comments.noteToParticipant.hint',
              'Adding comment to athlete is compulsory. Please explain the details, why entry was rejected or pass any information on behalf of OC. Comment will be sent to registration responsible as part of an automated notification informing about rejection. '
            )
          "
          data-test="noteToParticipant"
          variant="outlined"
        />
        <v-textarea
          v-model="internalNote"
          :label="
            $t(
              'events:entryAction.entryRejectForm.comments.internalNote.label',
              'Internal comment'
            )
          "
          :hint="
            $t(
              'events:entryAction.entryRejectForm.comments.internalNote.hint',
              'Internal comment will be visible to administrators from masterlist.'
            )
          "
          data-test="internalNote"
          variant="outlined"
        />

        <br />

        <div class="mb-2">
          <b>{{
            $t(
              "events:entryAction.entryRejectForm.alertNote.heading",
              "Please note:"
            )
          }}</b>
        </div>

        <AppAlertNotice center>
          {{
            $t(
              "events:entryAction.entryRejectForm.alertNote.text",
              "Entry will be marked as rejected by OC. Athlete will get an email notification that entry was rejected, including a comment to athlete. This function is reversible. It is possible to return rejected entry to waiting status and accept."
            )
          }}
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.entryRejectForm.confirmRejection.button",
          "Confirm rejection"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useEntryFeiActionsInjections } from "./compositions";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";

defineOptions({
  name: "EntryFeiRejectEntry"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const internalNote = ref(null);
const noteToParticipant = ref(null);
const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);

const entryLoading = ref(false);
const entry = ref(null);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      internalNote.value = null;
      noteToParticipant.value = null;
      responseType.value = null;
      mutationErrors.value = [];
    }
    modal.value = v;
  }
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.entryRejectForm.success.heading",
          "Reject entry on behalf of OC"
        ),
        main: t(
          "events:entryAction.entryRejectForm.success.mainMessage.text",
          "Entry was marked as rejected by OC"
        ),
        additional: t(
          "events:entryAction.entryRejectForm.success.additionalMessage.text",
          "Please note, athlete will get a notification of action."
        ),
        okBtn: t("events:entryAction.entryRejectForm.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.entryRejectForm.failed.heading",
          "Reject entry on behalf of OC"
        ),
        main: t(
          "events:entryAction.entryRejectForm.failed.mainMessage.text",
          "Action failed: entry cannot be rejected"
        ),
        reason: t(
          "events:entryAction.entryRejectForm.failed.reason.text",
          "The following reason has been reported:"
        ),
        additional: t(
          "events:entryAction.entryRejectForm.failed.additionalMessage.text",
          "Please contact technical support, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.entryRejectForm.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiRejectEntryFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
            internalNote
            noteToParticipant
            allowedMutations
            registration {
              id
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
            }
            competition {
              id
              code
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
    internalNote.value = data.entry.internalNote;
    noteToParticipant.value = data.entry.noteToParticipant;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  formSubmitted.value = true;

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryReject(
          $entryId: ID!
          $internalNote: String
          $noteToParticipant: String
        ) {
          entryReject(
            input: {
              entryId: $entryId
              internalNote: $internalNote
              noteToParticipant: $noteToParticipant
            }
          ) {
            entry {
              id
              status
              resolution
              internalNote
              noteToParticipant
              competitionId
              competition {
                id
                allowedMutations
              }
              canBeApprovedUnderHomeQuota
              allowedMutations
            }
          }
        }
      `,
      variables: {
        entryId: entry.value.id,
        internalNote: internalNote.value,
        noteToParticipant: noteToParticipant.value
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
